import React, { ChangeEvent, SVGProps, SyntheticEvent, useEffect, useRef, useState } from "react";
import { AccordionContentType, AccordionData } from "../../commonComponent/AccordionsComponent";
import { FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from 'primereact/inputtextarea';
import { Panel, PanelHeaderTemplateOptions } from "primereact/panel";
import { Menu } from "primereact/menu";
import { Editor } from "primereact/editor";
import { BsFileEarmarkRichtext, BsFiletypeHtml } from "react-icons/bs";
import { MdTextFields } from "react-icons/md";
import ErrorElement from "../../commonComponent/ErrorElement";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { getRootComponent } from "../../utilities/ElementUtils";

type AccordionDesignProps = {
    field: FormSectionElements;
    isEng: boolean;
    setAccordion: (accordions: AccordionData[], expandAll: boolean) => void;
    callBackElement: (programContainer: ProgramContainer) => void;
};

type AccordionErrorType = {
    index?: number;
    error?: string;
    paragraphErrors?: {
        index: number;
        error: string;
    }[];
};

type AccordionMenuItem = {
    label: string;
    value?: string;
    icon?: string | SVGProps<SVGAElement>;
    command?: (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => void;
};

type AccordionParagraphMenuItem = {
    label: AccordionContentType;
    value?: string;
    icon?: string | SVGProps<SVGAElement>;
    command?: (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => void;
};

const AccordionsDesignComponent = ({field, isEng, setAccordion, callBackElement}: AccordionDesignProps) => {
    const currentAccordions = isEng ? field.accordions : field.accordionsFRA;
    const emptyAccordion: AccordionData = {title: '', expand: false, paragraphs: [{content: '', type: 'Text'}]};
    const initAccordions = currentAccordions?.accordions ?? [emptyAccordion];
    const [contents, setContents] = useState<AccordionData[]>(initAccordions);
    const [expandAll, setExpandAll] = useState(!!currentAccordions?.expandAll);
    const [errors, setErrors] = useState<AccordionErrorType[]>([]);
    const [lastOperation, setLastOperation] = useState('');
    const [isLanguageTransition, setIsLanguageTransition] = useState(false);

    // useEffect(() => {
    //     if (!lastOperation)
    //         return;
    //     const validation = validateAccordionItem(lastOperation);
    //     if (!validation.length) {
    //         saveAccordions();
    //     }
    // }, [contents, expandAll]);
    
    const prevLanguage = useRef(isEng);
    
    useEffect(() => {
        if (prevLanguage.current !== isEng) {
            setIsLanguageTransition(true);
            
            const correctAccordions = isEng ? field.accordions : field.accordionsFRA;
            const correctContents = correctAccordions?.accordions ?? [emptyAccordion];
            const correctExpandAll = !!correctAccordions?.expandAll;
            
            setContents(correctContents);
            setExpandAll(correctExpandAll);
            
            prevLanguage.current = isEng;

            setTimeout(() => {
                setIsLanguageTransition(false);
            }, 150);
        }
    }, [isEng, field.accordions, field.accordionsFRA]);

    useEffect(() => {
        if (!isLanguageTransition) {
            setContents(currentAccordions?.accordions ?? [emptyAccordion]);
            setExpandAll(!!currentAccordions?.expandAll);
        }
    }, [currentAccordions, isLanguageTransition]);

    const saveAccordions = (accordions: AccordionData[], expandall = false) => {
        if (isLanguageTransition) {
            return;
        }
        
        const isValidStructure = Array.isArray(accordions) && accordions.every(accordion => 
            typeof accordion === 'object' && 
            accordion !== null &&
            accordion.hasOwnProperty('title') && 
            accordion.hasOwnProperty('paragraphs') &&
            Array.isArray(accordion.paragraphs)
        );
        
        if (!isValidStructure) {
            return;
        }
        
        setAccordion(accordions, expandall);
        const validation = validateAccordionItem(accordions, lastOperation);
    }

    const getItemAndParagraphIndex = (id: string): {itemIndex: number, paragraphIndex: number} => {
        const regex = /(-\d+)?(-\d+)$/;
        const match = id.match(regex);
        if (match.length) {
            const value = match[0].substring(1).split("-");
            return {itemIndex: parseInt(value[0]), paragraphIndex: parseInt(value?.[1] || '-1')};
        }
        return {itemIndex: -1, paragraphIndex: -1};
    }

    const handleDeleteItem = (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => {
        const menuItem = item.item?.value ? item.item.value : item.item.label;
        const {itemIndex, paragraphIndex} = getItemAndParagraphIndex(menuItem);
        if (itemIndex === -1 && paragraphIndex === -1) {
            return;
        } else if (paragraphIndex === -1) {
            let items = contents.filter((content, index) => index !== itemIndex);
            if (!items.length) {
                items = [emptyAccordion];
            }
            //setContents(items);
            saveAccordions(items, expandAll)
        } else {
            const items = contents.map((content, index) => {
                if (index === itemIndex) {
                    let paragraphs = content.paragraphs.filter((p, pindex) => pindex !== paragraphIndex);
                    if (!paragraphs.length) {
                        paragraphs = [{content: '', type: 'Text'}];
                    }
                    return {...content, paragraphs};
                }
                return content;
            });
            saveAccordions(items, expandAll)
        }
    }

    const handleAddParagraph = (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => {
        const menuItem = item.item?.value ? item.item.value : item.item.label;
        const {itemIndex, paragraphIndex} = getItemAndParagraphIndex(menuItem);
        if (itemIndex === -1) {
            return
        }
        const validation = validateAccordionItem(contents);
        if (validation.length) {
            return;
        }

        // add new paragraph
        const newContents = contents.map((content, index) => {
            if (index === itemIndex) {
                const paragraphs = [...content.paragraphs];
                paragraphs.push({content: '', type: 'Text'});
                return {...content, paragraphs};
            }
            return content;
        })
        setLastOperation('AddPragraph');
        //setContents(newContents);
        saveAccordions(newContents, expandAll);
    };

    const handleAddItem = () => {
        const validation = validateAccordionItem(contents);
        if (validation.length) {
            return;
        }
        
        const newContents = [...contents];
        newContents.push(emptyAccordion);
        setLastOperation('AddItem');
        setContents(newContents);
        return;
    }

    const handleParagraphTypeChange = (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => {
        const menuItem = item.item?.value ? item.item.value : item.item.label;
        const {itemIndex, paragraphIndex} = getItemAndParagraphIndex(menuItem);
        if (itemIndex === -1 || paragraphIndex === -1) return;

        let newType = "Html";
        if (item.item.label == "Texte enrichi" || item.item.label == "Rich Text") {newType = "Rich Text"}
        else if (item.item.label == "Texte" || item.item.label == "Text") {newType = "Text"}

        // const newContents = contents.map((content, cidx) => {
        //     if (cidx === itemIndex) {
        //         const newParagraphs = content.paragraphs.map((paragraph, pindex) =>{
        //             return pindex === paragraphIndex ? {...paragraph, type: newType as AccordionContentType} : paragraph;
        //         });
        //         return {...content, paragraphs: newParagraphs}
        //     }
        //     return content;
        // });
        // setContents(newContents);
        contents[itemIndex].paragraphs[paragraphIndex].type = newType as AccordionContentType;
        saveAccordions(contents, expandAll);
    }

    const handleExpandChange = (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => {
        const menuItem = item.item?.value ? item.item.value : item.item.label;
        const {itemIndex, paragraphIndex} = getItemAndParagraphIndex(menuItem);
        if (itemIndex === -1) return;
        
        const newContents = contents.map((content, index) => {
            return index === itemIndex ? {...content, expand: !content.expand} : content;
        });
        saveAccordions(newContents, expandAll);
    }

    const handleItemMove = (item: {item: AccordionMenuItem, originalEvent: SyntheticEvent}) => {
        const menuItem = item.item?.value ? item.item.value : item.item.label;
        const {itemIndex, paragraphIndex} = getItemAndParagraphIndex(menuItem);
        if (itemIndex === -1) return;

        let targetIndex;
        if (paragraphIndex !== -1) {
            targetIndex = menuItem.startsWith("up") ? paragraphIndex - 1 : paragraphIndex + 1;
            if (!contents[itemIndex].paragraphs[paragraphIndex].content || !contents[itemIndex].paragraphs[targetIndex].content) return;
        } else {
            targetIndex = menuItem.startsWith("up") ? itemIndex - 1 : itemIndex + 1;
            if (!contents[itemIndex].title || !contents[targetIndex].title) return;
        }

        if (paragraphIndex === -1) {
            const newContents = contents.map((content, index) => {
                if (index === itemIndex || index === targetIndex) {
                    return index === itemIndex ? contents[targetIndex] : contents[itemIndex];
                }
                return content;
            });
            // setContents(newContents);
            saveAccordions(newContents, expandAll);
        } else {
            const newContents = contents.map((content, index) => {
                if (index === itemIndex) {
                    const paragraphs = content.paragraphs.map((p, pindex) => {
                        if (pindex === paragraphIndex || pindex === targetIndex) {
                            return pindex === paragraphIndex ? content.paragraphs[targetIndex] : content.paragraphs[paragraphIndex];
                        }
                        return p;
                    });
                    return {...content, paragraphs};
                }
                return content;
            });
            //setContents(newContents);
            saveAccordions(newContents, expandAll);
        }
    }

    const HeaderMenuItems = {
        en: [{
                label: 'Add paragraph', 
                icon: 'pi pi-plus',
                value: 'addParagraph',
                command: handleAddParagraph,
            }, 
            {
                label: 'Delete accordion item', 
                icon: 'pi pi-trash',
                value: 'del',
                command: handleDeleteItem,
            },
            {separator: true},
            {
                label: 'Initial expand',
                value: 'expand',
                icon: 'pi pi-expand',
                command: handleExpandChange,
            },
            {separator: true},
            {
                label: 'Move up',
                value: 'up',
                icon: 'pi pi-arrow-up',
                command: handleItemMove,
            },
            {
                label: 'Move down',
                value: 'down',
                icon: 'pi pi-arrow-down',
                command: handleItemMove,
            },
        ],
        fr: [{
                label: 'Ajouter un paragraphe', 
                value: 'addParagraph',
                icon: 'pi pi-plus',
                command: handleAddParagraph,
            },
            {
                label: "Supprimer l'élément accordéon", 
                value: 'del',
                icon: 'pi pi-trash',
                command: handleDeleteItem,
            },
            {separator: true},
            {
                label: 'Expansion initiale',
                value: 'expand',
                icon: 'pi pi-expand',
                command: handleExpandChange,
            },
            {separator: true},
            {
                label: 'Monter',
                value: 'up',
                icon: 'pi pi-arrow-up',
                command: handleItemMove,
            },
            {
                label: 'Descendre',
                value: 'down',
                icon: 'pi pi-arrow-down',
                command: handleItemMove,
            },
        ],
    };

    const paragraphMenuItems = {
        en: [{
                label: 'Html', 
                value: 'html',
                icon: <BsFiletypeHtml />,
                command: handleParagraphTypeChange,
            },
            {
                label: 'Text',
                value: 'text',
                icon: <MdTextFields />,
                command: handleParagraphTypeChange,
            },
            {
                label: 'Rich Text',
                value: 'richtext',
                icon: <BsFileEarmarkRichtext />,
                command: handleParagraphTypeChange,
            },
            {separator: true},
            {
                label: 'Delete',
                value: 'del',
                icon: 'pi pi-trash',
                command: handleDeleteItem,
            },
            {separator: true},
            {
                label: 'Move up',
                value: 'up',
                icon: 'pi pi-arrow-up',
                command: handleItemMove,
            },
            {
                label: 'Move down',
                value: 'down',
                icon: 'pi pi-arrow-down',
                command: handleItemMove,
            },
        ],
        fr: [{
                label: 'Html',
                value: 'html',
                icon: <BsFiletypeHtml />,
                command: handleParagraphTypeChange,
            },
            {
                label: 'Texte',
                value: 'text',
                icon: <MdTextFields />,
                command: handleParagraphTypeChange,
            },
            {
                label: 'Texte enrichi',
                value: 'richtext',
                icon: <BsFileEarmarkRichtext />,
                command: handleParagraphTypeChange,
            },
            {separator: true},
            {
                label: 'Supprimer',
                value: 'del',
                icon: 'pi pi-trash',
                command: handleDeleteItem,
            },
            {separator: true},
            {
                label: 'Monter',
                value: 'up',
                icon: 'pi pi-arrow-up',
                command: handleItemMove,
            },
            {
                label: 'Descendre',
                value: 'down',
                icon: 'pi pi-arrow-down',
                command: handleItemMove,
            },
        ],
    };

    const validateAccordionItem = (accordions: AccordionData[], operation?: string) => {
        const accordionErrors = contents.reduce((sum, content, index) => {
            let error: AccordionErrorType = {};
            if (!content.title && (operation !== 'AddItem' || (index + 1) !== contents.length)) {
                error.index = index;
                error.error = isEng? 'Accordion item must have a title' : "L'article en accordéon doit avoir un titre";
            }

            if (operation !== 'AddItem' || (index + 1) !== contents.length) {
                content.paragraphs.forEach((p, pindex) => {
                    if (!p.content && (operation !== 'AddPragraph' || (pindex + 1) !== content.paragraphs.length)) {
                        if (!error.index) {
                            error.index = index;
                        }
                        if (!error.paragraphErrors) {
                            error.paragraphErrors = [];
                        }
                        error.paragraphErrors.push({index: pindex, error: isEng? 'Paragraph must have content' : 'Le paragraphe doit avoir du contenu'});
                    }
                });
            }

            if (error.index >= 0) {
                sum.push(error);
            }
            return sum;
        }, []);

        setErrors(accordionErrors);
        return accordionErrors;
    };

    const handleContentChange = (value, index1: number, index2: number) => {
        if (isLanguageTransition) {
            return;
        }
        
        const newContents = [...contents];
        newContents[index1] = {
            ...newContents[index1],
            paragraphs: newContents[index1].paragraphs.map((p, idx) => 
                idx === index2 ? {...p, content: value} : p
            )
        };
        
        setContents(newContents);
        saveAccordions(newContents, expandAll);
    }

    const handleTitleChange = (e: ChangeEvent<HTMLInputElement>, index?: number) => {
        if (isLanguageTransition) {
            return;
        }
        
        const value = e.target.value;
        const newContents = contents.map((content, idx) => 
            idx === index ? {...content, title: value} : content
        );
        
        setContents(newContents);
        setLastOperation('Change');
        saveAccordions(newContents, expandAll);
    }

    const getParagraphError = (itemIndex: number, paragraphIndex): string | undefined => {
        const error = errors.filter((err, index) => err.index === itemIndex)
            .map(err => {
                if (err.paragraphErrors?.length) {
                    const perrors = err.paragraphErrors.filter((perr, pindex) => perr.index === paragraphIndex);
                    return perrors?.[0]?.error
                }
                return undefined;
            })
            .filter(Boolean);
        return error?.[0];
    }

    const headerTemplate = (options: PanelHeaderTemplateOptions) => {
        const backgroundColor = options.props.id?.startsWith('accordion-item')? '#d3d3d3' : '#f3f3f3';
        const lang = isEng? 'en': 'fr';
        const menuRef = useRef(null);

        const {itemIndex, paragraphIndex} = getItemAndParagraphIndex(options.props.id);

        const getSuffix = () => {
            if (options.props.id?.startsWith('accordion-item')) {
                return options.props.id.substring('accordion-item-'.length);
            } else if (options.props.id?.startsWith('accordion-paragraph')) {
                return options.props.id.substring('accordion-paragraph-'.length);
            }
            return '';
        }

        const getMenuItems = (itemIndex: number, paragraphIndex: number) => {
            const menuItems = options.props.id?.startsWith('accordion-item') ? HeaderMenuItems[lang]: paragraphMenuItems[lang];
            return menuItems.map((item, index) => {
                if (options.props.id?.startsWith('accordion-item') ) {
                    if (contents?.length === 1 && (item.value === 'up' || item.value === 'down')) return null;
                    if ((itemIndex === 0 && item.value === 'up') || (itemIndex === (contents.length -1) && item.value === 'down')) return null; 
                }
                if (options.props.id?.startsWith('accordion-paragraph')) {
                    if (contents[itemIndex]?.paragraphs?.length === 1 && (item.value === 'up' || item.value === 'down')) return null;
                    if ((paragraphIndex === 0 && item.value === 'up') || (paragraphIndex === (contents[itemIndex]?.paragraphs?.length -1) && item.value === 'down')) return null; 
                }
                
                const value = (item.value ?? '') + '-' + getSuffix();
                const icon = contents[itemIndex].expand ? 'pi pi-window-minimize' : 'pi pi-window-maximize';
                const label = contents[itemIndex].expand ? 
                    (isEng ? 'Initial collapse' : 'Effondrement initial') : (isEng? 'Initial Expand' : 'Expansion initiale');
                if (options.props.id?.startsWith('accordion-item')) {
                    return item.value === 'expand'? {...item, label, value, icon} : {...item, value};
                } else {
                    return {...item, value};
                }
            }).filter(Boolean);
        };

        return (
            <div style={{display: 'flex', justifyContent: 'space-between', padding: '0.5rem 1rem', background: backgroundColor, border: '1px solid', borderTopLeftRadius: '2px',borderTopRightRadius: '2px'}}>
                <div style={{gap: '2'}}>
                    {options.props.id?.startsWith('accordion-item') && (
                        <>
                        <span className="font-bold">{(isEng ? 'Accordion Item ' : 'Article accordéon ') + (itemIndex + 1)}</span>
                        {contents[itemIndex]?.expand && <span style={{marginLeft: '1.5rem'}}>[{isEng ? 'Expand' : 'Développer'}]</span>}
                        </>
                    )}
                    {options.props.id?.startsWith('accordion-paragraph') && (
                        <>
                            <span className="font-bold">{(isEng ? 'Accordion paragraph ' : 'Article Paragraphe ') + (paragraphIndex + 1)}</span>
                            <span style={{marginLeft: '1.5rem'}}>[{contents[itemIndex]?.paragraphs?.[paragraphIndex]?.type}]</span>
                        </>
                    )}
                </div>
                <div>
                    <Menu model={getMenuItems(itemIndex, paragraphIndex)} popup ref={menuRef} id={`accordion-menu-${getSuffix()}`} style={{minWidth: '15rem'}} />
                    <button 
                        className="p-panel-header-icon p-link mr-2" 
                        onClick={(e) => menuRef?.current?.toggle(e)}
                    >
                        <span className="pi pi-cog"></span>
                    </button>
                    {options.togglerElement}
                </div>
            </div>
        );
    };

    return (
        <div className="ontario-margin-top-24-!">
            {/* <p>{isEng? "Accordion items:" : "Articles d'accordéon :"}</p> */}
            {contents.map((content, index) => (
                <Panel 
                    key={`accordion-item-${field.Id}-${isEng ? 'en' : 'fr'}-${index}`}
                    headerTemplate={headerTemplate} 
                    title={`Accordion item ${index + 1}`} 
                    id={`accordion-item-${index}`} 
                    toggleable
                >
                    <div className="ontario-paddding-top-16-!">
                        <ErrorElement
                            display={!!errors.filter(err => err.index === index)?.[0]?.error} 
                            message={errors.filter(err => err.index === index)?.[0]?.error}
                            id={`accordion-item-${index}-error`}
                        />
                        <InputText
                            key={`title-${field.Id}-${isEng ? 'en' : 'fr'}-accordion-${index}`}
                            id={`accordion-title-${index}`}
                            style={{width: '70%'}} 
                            value={content.title}
                            placeholder={isEng? 'Accordion item title' : "Titre de l'article en accordéon"}
                            onChange={(e) => handleTitleChange(e, index)}
                        />

                        {content.paragraphs.map((p, pidx) => (
                            <div key={`paragraph-container-${index}-${pidx}`} className="accordion-content-card">
                                <Panel 
                                    key={`accordion-paragraph-${field.Id}-${isEng ? 'en' : 'fr'}-${index}-${pidx}`}
                                    headerTemplate={headerTemplate}
                                    title={`Accordion paragraph ${pidx + 1}`} // Fix: pidx + 1 instead of just pidx
                                    id={`accordion-paragraph-${index}-${pidx}`}
                                    className={p.type==='Rich Text' ? 'resizable-panel' : ''}
                                    toggleable
                                >
                                    <ErrorElement
                                        display={!!getParagraphError(index, pidx)} 
                                        message={getParagraphError(index, pidx) ?? ''}
                                        id={`accordion-item-${index}-para-${pidx}-error`}
                                    />
                                    {(p.type === 'Html' || p.type === 'Text') && (
                                        <InputTextarea 
                                            key={`textarea-${field.Id}-${isEng ? 'en' : 'fr'}-${index}-${pidx}`}
                                            value={p.content}
                                            onChange={(e) => handleContentChange(e.target.value, index, pidx)}
                                            style={{ height: '100px', width: '100%' }} 
                                        />
                                    )}

                                    {p?.type === 'Rich Text' && (
                                        <Editor 
                                            key={`editor-${field.Id}-${isEng ? 'en' : 'fr'}-accordion-${index}-paragraph-${pidx}-${isLanguageTransition ? 'transition' : 'stable'}`}
                                            value={p.content ?? ''}
                                            onTextChange={(e) => {
                                                if (!isLanguageTransition && document.hasFocus()) {
                                                    handleContentChange(e.htmlValue, index, pidx);
                                                }
                                            }}
                                        />
                                    )}
                                </Panel>
                            </div>
                        ))}
                    </div>
                </Panel>
            ))}
            <div style={{display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', gap: '3' }}>
                <div style={{display: 'flex', flexWrap: 'nowrap', alignItems: 'center'}}>
                    {/* @ts-ignore */}
                    <Button
                        className="p-button-rounded p-button-secondary"
                        style={{
                            cursor: "pointer",
                            margin: "5px",
                            fontSize: "90%",
                            border: "1px solid #64748B",
                            color: "#64748B",
                            background: "white"
                        }}
                        onClick={handleAddItem}
                    >
                        Add accordion item
                    </Button>
                </div>
                {contents?.length > 1 && (
                    <div style={{display: 'flex', flexWrap: 'nowrap', alignItems: 'center', marginLeft: '3rem'}}>
                        <Checkbox inputId={`expand-all-${field.Id}`} name={`expand-all-${field.Id}`} value="Pepper" onChange={() => setExpandAll(!expandAll)} checked={expandAll} />
                        <label htmlFor={`expand-all-${field.Id}`} className="ml-2">{isEng ? 'Expand All' : 'Élargir tout'}</label>
                    </div>
                )}
            </div>
        </div>
    )
};

export default AccordionsDesignComponent;